import { Injectable } from '@angular/core';
import { Observable, Subject, from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { toCanvas as qrCodeToCanvas } from 'qrcode';
import { LabelLayoutConfig, PrintData } from './niimbot-printer.models';

// Tipos para evitar importação estática da biblioteca
type NiimbotBluetoothClientType = any;
type NiimbotSerialClientType = any;
type ImageEncoderType = any;
type UtilsType = any;
type RequestCommandIdType = any;
type ResponseCommandIdType = any;
type PrintDirectionType = 'top' | 'left' | 'right' | 'bottom';

export interface NiimbotConnectionStatus {
  connected: boolean;
  transport?: 'ble' | 'serial';
  serialNumber?: string;
}

export interface NiimbotPrintProgress {
  page: number;
  pagesTotal: number;
  pagePrintProgress: number;
  pageFeedProgress: number;
}

export interface NiimbotPreset {
  width: number;
  height: number;
  unit: string;
  dpmm: number;
  printDirection?: PrintDirectionType;
  shape?: string;
  title?: string;
  split?: string;
  splitParts?: number;
  tailPos?: string;
  tailLength?: number;
  mirror?: string;
}

export interface NiimbotLabelConfig {
  width: number;
  height: number;
  printDirection?: 'top' | 'left' | 'right' | 'bottom';
  shape?: string;
}

/**
 * Service para integração com impressoras Niimbot via Bluetooth ou Serial
 *
 * Exemplo de uso:
 *
 * ```typescript
 * constructor(private niimbotService: NiimbotPrinterService) {}
 *
 * // Conectar via Bluetooth
 * this.niimbotService.connectBluetooth().subscribe({
 *   next: () => console.log('Conectado'),
 *   error: (err) => console.error('Erro ao conectar', err)
 * });
 *
 * // Imprimir QR Code
 * this.niimbotService.printQRCode('Texto do QR Code').subscribe({
 *   next: () => console.log('Impressão concluída'),
 *   error: (err) => console.error('Erro ao imprimir', err)
 * });
 *
 * // Escutar logs
 * this.niimbotService.logs$.subscribe(log => console.log(log));
 *
 * // Escutar progresso de impressão
 * this.niimbotService.printProgress$.subscribe(progress => {
 *   console.log(`Página ${progress.page}/${progress.pagesTotal}`);
 * });
 *
 * // Desconectar
 * this.niimbotService.disconnect().subscribe();
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class NiimbotPrinterService {
  private client: NiimbotBluetoothClientType | NiimbotSerialClientType | null = null;
  private niimbotLib: any = null; // Cache da biblioteca carregada dinamicamente
  private connectionStatusSubject = new Subject<NiimbotConnectionStatus>();
  private printProgressSubject = new Subject<NiimbotPrintProgress>();
  private logSubject = new Subject<string>();
  private currentConnectionStatus: NiimbotConnectionStatus = {
    connected: false,
  };
  private readonly STORAGE_KEY = 'niimbot-last-connection';
  private autoReconnectEnabled: boolean = true;

  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  public printProgress$ = this.printProgressSubject.asObservable();
  public logs$ = this.logSubject.asObservable();

  private defaultPreset: NiimbotPreset = {
    width: 50,
    height: 30,
    unit: 'mm',
    dpmm: 8,
    printDirection: 'top',
    shape: 'rect',
  };

  /**
   * Carrega a biblioteca @mmote/niimbluelib do CDN (window.niimbluelib)
   * A biblioteca é carregada via script tag no index.html
   */
  private async loadNiimbotLib(): Promise<any> {
    if (this.niimbotLib) {
      return this.niimbotLib;
    }

    // Verificar se estamos em um ambiente de browser
    if (typeof window === 'undefined') {
      throw new Error('Biblioteca da impressora só está disponível no navegador');
    }

    try {
      this.log('Carregando biblioteca do CDN...');

      // Aguardar um pouco para garantir que o script CDN foi carregado
      let attempts = 0;
      const maxAttempts = 50; // 5 segundos no total (50 * 100ms)

      // Verificar diferentes possíveis nomes da variável global
      while (attempts < maxAttempts) {
        // Tentar window.niimbluelib (mais comum)
        if (window.niimbluelib) {
          this.niimbotLib = window.niimbluelib;
          break;
        }
        // Tentar window.NiimbotLib (alternativa)
        if (window.NiimbotLib) {
          this.niimbotLib = window.NiimbotLib;
          break;
        }
        // Tentar variável global niimbluelib (sem window, para builds UMD)
        if (typeof niimbluelib !== 'undefined') {
          this.niimbotLib = niimbluelib;
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      // Verificar se a biblioteca está disponível (tentativa final)
      if (!this.niimbotLib) {
        // Tentar uma última vez acessar diretamente
        this.niimbotLib = window.niimbluelib || window.NiimbotLib || (typeof niimbluelib !== 'undefined' ? niimbluelib : undefined);

        if (!this.niimbotLib) {
          throw new Error('Biblioteca não encontrada. Verifique se o script CDN foi carregado corretamente no index.html e se há conexão com a internet.');
        }
      }

      // Verificar se a biblioteca foi carregada corretamente
      if (!this.niimbotLib) {
        throw new Error('Biblioteca carregada mas está vazia');
      }

      // Verificar se as classes necessárias estão disponíveis
      const hasBluetoothClient = !!this.niimbotLib.NiimbotBluetoothClient;
      const hasSerialClient = !!this.niimbotLib.NiimbotSerialClient;

      if (!hasBluetoothClient && !hasSerialClient) {
        // Log detalhado para debug
        console.warn('Estrutura da biblioteca carregada:', {
          keys: Object.keys(this.niimbotLib),
          availableClasses: {
            NiimbotBluetoothClient: !!this.niimbotLib.NiimbotBluetoothClient,
            NiimbotSerialClient: !!this.niimbotLib.NiimbotSerialClient,
            ImageEncoder: !!this.niimbotLib.ImageEncoder,
            Utils: !!this.niimbotLib.Utils
          }
        });
        throw new Error('Classes necessárias (NiimbotBluetoothClient ou NiimbotSerialClient) não encontradas na biblioteca');
      }

      this.log('Biblioteca carregada com sucesso do CDN');
      return this.niimbotLib;
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      const errorStack = error?.stack || '';
      const errorName = error?.name || '';

      console.error('Erro detalhado ao carregar biblioteca:', {
        name: errorName,
        message: errorMessage,
        stack: errorStack,
        error: error,
        type: typeof error,
        windowHasLib: !!window.niimbluelib
      });

      this.log(`Erro ao carregar biblioteca: ${errorMessage}`);

      // Fornecer mensagem de erro mais informativa
      let userMessage = 'Não foi possível carregar a biblioteca da impressora';

      if (errorMessage.includes('não encontrada no window') ||
          errorMessage.includes('script CDN')) {
        userMessage = 'Biblioteca da impressora não encontrada. Verifique se o script CDN foi carregado corretamente no index.html e se há conexão com a internet.';
      } else if (errorMessage.includes('Classes necessárias')) {
        userMessage = 'A biblioteca foi carregada mas não contém as classes esperadas. A versão do CDN pode estar incompatível.';
      }

      throw new Error(`${userMessage}\n\nDetalhes: ${errorMessage}`);
    }
  }

  /**
   * Round width to nearest multiple of 8 (required by NIIMBOT printers)
   */
  private roundToMultipleOf8(width: number): number {
    return Math.ceil(width / 8) * 8;
  }

  /**
   * Replace placeholders in text with current date/time
   */
  private replacePlaceholders(text: string): string {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const timeStr = now.toTimeString().split(' ')[0]; // HH:MM:SS

    return text
      .replace(/{dt}/g, `${dateStr} ${timeStr}`)
      .replace(/{date}/g, dateStr)
      .replace(/{time}/g, timeStr);
  }

  /**
   * Center object horizontally and vertically
   */
  private centerObject(
    canvasWidth: number,
    canvasHeight: number,
    objWidth: number,
    objHeight: number
  ): { left: number; top: number } {
    return {
      left: Math.round((canvasWidth - objWidth) / 2),
      top: Math.round((canvasHeight - objHeight) / 2),
    };
  }

  /**
   * Generate QR code canvas
   */
  private async generateQRCodeCanvas(
    text: string,
    width: number,
    height: number,
    options?: {
      errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
      darkColor?: string;
      lightColor?: string;
    }
  ): Promise<HTMLCanvasElement> {
    const qrCanvas = document.createElement('canvas');
    qrCanvas.width = width;
    qrCanvas.height = height;

    await qrCodeToCanvas(qrCanvas, text, {
      width: width,
      margin: 0,
      color: {
        dark: options?.darkColor || '#292929',
        light: options?.lightColor || '#ffffff',
      },
      errorCorrectionLevel: options?.errorCorrectionLevel || 'M',
    });

    return qrCanvas;
  }

  /**
   * Create canvas with QR code and optional text above
   */
  private async createLabelCanvas(
    qrText: string,
    preset: NiimbotPreset,
    textAbove?: string
  ): Promise<HTMLCanvasElement> {
    // Convert mm to pixels using dpmm (dots per mm)
    let widthPixels = Math.round(preset.width * preset.dpmm);
    const heightPixels = Math.round(preset.height * preset.dpmm);

    // Ensure width is multiple of 8 (required by NIIMBOT printers)
    widthPixels = this.roundToMultipleOf8(widthPixels);

    // Create main canvas
    const canvas = document.createElement('canvas');
    canvas.width = widthPixels;
    canvas.height = heightPixels;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Fill background with white
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Replace placeholders in QR code text
    const processedText = this.replacePlaceholders(qrText);

    // Calculate available space for text and QR code
    const marginPercent = 0.12; // Reduced margin to allow larger QR code
    const maxWidth = Math.round(canvas.width * (1 - 2 * marginPercent));
    const maxHeight = Math.round(canvas.height * (1 - 2 * marginPercent));

    // If text above is provided, reserve space for it
    let textHeight = 0;
    let textY = 0;
    const textPadding = Math.round(heightPixels * 0.02); // Padding between text and QR code (2% of height)

    if (textAbove && textAbove.trim()) {
      // Calculate text height based on canvas size
      // For 50x30mm @ 8dpmm = 400x240px, text should be ~15-18% of height (similar to example.json)
      // This gives approximately 36-43px height for text area
      textHeight = Math.max(Math.round(heightPixels * 0.15), 30);
      // Move text higher (reduce top margin) - use smaller margin for text position
      textY = Math.round(canvas.height * 0.08); // Moved up - only 8% from top instead of marginPercent
    }

    // Calculate QR code size considering text space
    // Use more of the available space for QR code
    const availableHeightForQR = maxHeight - (textHeight > 0 ? textHeight + textPadding : 0);
    // Increase QR code size by using more of available space
    const maxSize = Math.min(maxWidth, Math.round(availableHeightForQR * 1.0));

    // Calculate QR code dimensions
    let qrWidth = maxSize;
    let qrHeight = maxSize;

    // Generate QR code
    const qrCanvas = await this.generateQRCodeCanvas(
      processedText,
      qrWidth,
      qrHeight
    );

    // Calculate positions
    let qrY: number;
    if (textAbove && textAbove.trim()) {
      // Position QR code below the text
      qrY = textY + textHeight + textPadding;
    } else {
      // Center QR code vertically
      qrY = Math.round((canvas.height - qrHeight) / 2);
    }

    // Center QR code horizontally
    const qrX = Math.round((canvas.width - qrWidth) / 2);

    // Draw text above QR code if provided
    if (textAbove && textAbove.trim()) {
      ctx.fillStyle = 'black';
      // Font size based on canvas height (similar to example.json where fontSize is 40 for 240px height)
      // For 240px height, fontSize 40 = ~16.7% of height
      // Scale proportionally: fontSize = heightPixels * 0.167
      const fontSize = Math.round(heightPixels * 0.167);
      ctx.font = `normal ${fontSize}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      // Draw text centered horizontally
      const textX = canvas.width / 2;
      ctx.fillText(textAbove.trim(), textX, textY);
    }

    // Draw QR code on main canvas
    ctx.drawImage(qrCanvas, qrX, qrY);

    return canvas;
  }

  /**
   * Connect to printer via Bluetooth
   */
  connectBluetooth(): Observable<void> {
    return from(this.connectBluetoothAsync()).pipe(
      catchError((error) => {
        this.log(`Erro ao conectar via Bluetooth: ${error}`);
        return throwError(() => error);
      })
    );
  }

  private async connectBluetoothAsync(): Promise<void> {
    const lib = await this.loadNiimbotLib();

    // Acessar NiimbotBluetoothClient da biblioteca CDN
    const NiimbotBluetoothClient = lib.NiimbotBluetoothClient;

    if (!NiimbotBluetoothClient) {
      throw new Error('BluetoothClient não encontrado na biblioteca. Verifique se a biblioteca está corretamente carregada do CDN.');
    }

    if (this.client) {
      await this.client.disconnect();
    }

    this.client = new NiimbotBluetoothClient();
    this.setupClientEvents(lib);

    try {
      await this.client.connect();
      this.log('Conectado via Bluetooth');
    } catch (error) {
      this.client = null;
      throw error;
    }
  }

  /**
   * Connect to printer via Serial
   */
  connectSerial(): Observable<void> {
    return from(this.connectSerialAsync()).pipe(
      catchError((error) => {
        this.log(`Erro ao conectar via Serial: ${error}`);
        return throwError(() => error);
      })
    );
  }

  private async connectSerialAsync(): Promise<void> {
    const lib = await this.loadNiimbotLib();

    // Acessar NiimbotSerialClient da biblioteca CDN
    const NiimbotSerialClient = lib.NiimbotSerialClient;

    if (!NiimbotSerialClient) {
      throw new Error('SerialClient não encontrado na biblioteca. Verifique se a biblioteca está corretamente carregada do CDN.');
    }

    if (this.client) {
      await this.client.disconnect();
    }

    this.client = new NiimbotSerialClient();
    this.setupClientEvents(lib);

    try {
      await this.client.connect();
      this.log('Conectado via Serial');
    } catch (error) {
      this.client = null;
      throw error;
    }
  }

  /**
   * Setup client event handlers
   */
  private setupClientEvents(lib: any): void {
    if (!this.client || !lib) {
      return;
    }

    // Acessar as classes da biblioteca CDN
    const Utils = lib.Utils;
    const RequestCommandId = lib.RequestCommandId;
    const ResponseCommandId = lib.ResponseCommandId;
    const NiimbotBluetoothClient = lib.NiimbotBluetoothClient;

    this.client.on('packetsent', (e: any) => {
      this.log(
        `>> ${Utils.bufToHex(e.packet.toBytes())} (${
          RequestCommandId[e.packet.command]
        })`
      );
    });

    this.client.on('packetreceived', (e: any) => {
      this.log(
        `<< ${Utils.bufToHex(e.packet.toBytes())} (${
          ResponseCommandId[e.packet.command]
        })`
      );
    });

    this.client.on('connect', async (event: any) => {
      this.log('Conectado ' + event.info.deviceName);

      // Obter número serial da impressora
      let serialNumber: string | undefined;
      try {
        if (this.client) {
          serialNumber = await this.client.abstraction.getPrinterSerialNumber();
          this.log(`Número serial: ${serialNumber}`);
        }
      } catch (error) {
        this.log(`Erro ao obter número serial: ${error}`);
      }

      // Determinar o tipo de transporte baseado na instância do cliente
      const isBluetooth = NiimbotBluetoothClient && this.client instanceof NiimbotBluetoothClient;

      const status: NiimbotConnectionStatus = {
        connected: true,
        transport: isBluetooth ? 'ble' : 'serial',
        serialNumber: serialNumber,
      };
      this.currentConnectionStatus = status;
      this.connectionStatusSubject.next(status);

      // Salvar informações da última conexão para reconexão automática
      this.saveLastConnection(status);
    });

    this.client.on('disconnect', () => {
      this.log('Desconectado');
      const status: NiimbotConnectionStatus = {
        connected: false,
        serialNumber: undefined,
      };
      this.currentConnectionStatus = status;
      this.connectionStatusSubject.next(status);
    });

    this.client.on('printprogress', (e: any) => {
      this.printProgressSubject.next({
        page: e.page,
        pagesTotal: e.pagesTotal,
        pagePrintProgress: e.pagePrintProgress,
        pageFeedProgress: e.pageFeedProgress,
      });
      this.log(
        `Página ${e.page}/${e.pagesTotal}, Progresso de impressão ${e.pagePrintProgress}%, Progresso de alimentação ${e.pageFeedProgress}%`
      );
    });
  }

  /**
   * Disconnect from printer
   */
  disconnect(): Observable<void> {
    return from(this.disconnectAsync()).pipe(
      catchError((error) => {
        this.log(`Erro ao desconectar: ${error}`);
        return throwError(() => error);
      })
    );
  }

  private async disconnectAsync(): Promise<void> {
    const client = this.client;
    if (!client) return;

    // O client local é solto no finally: se o transporte já caiu, o
    // disconnect() da lib (CDN, Web Bluetooth/Serial) rejeita ou nem resolve, e
    // manter a referência deixaria a UI presa em "Conectada", sem caminho de
    // volta. Quem falha aqui é a despedida, não a desconexão.
    try {
      await client.disconnect();
    } finally {
      this.client = null;
      const status: NiimbotConnectionStatus = { connected: false, serialNumber: undefined };
      this.currentConnectionStatus = status;
      this.connectionStatusSubject.next(status);
    }
  }

  /**
   * Check if printer is connected
   */
  isConnected(): boolean {
    return this.client !== null;
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): NiimbotConnectionStatus {
    // Retornar status atual mantido no service (inclui serialNumber)
    return { ...this.currentConnectionStatus };
  }

  /**
   * Save last connection information to localStorage
   */
  private saveLastConnection(status: NiimbotConnectionStatus): void {
    try {
      if (status.connected && status.transport) {
        const connectionInfo = {
          transport: status.transport,
          serialNumber: status.serialNumber,
          timestamp: Date.now(),
        };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(connectionInfo));
        this.log('Informações da conexão salvas para reconexão automática');
      }
    } catch (error) {
      this.log(`Erro ao salvar informações da conexão: ${error}`);
    }
  }

  /**
   * Get last connection information from localStorage
   */
  private getLastConnection(): {
    transport: 'ble' | 'serial';
    serialNumber?: string;
    timestamp: number;
  } | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      this.log(`Erro ao ler informações da última conexão: ${error}`);
    }
    return null;
  }

  /**
   * Enable or disable auto-reconnect
   */
  setAutoReconnect(enabled: boolean): void {
    this.autoReconnectEnabled = enabled;
    this.log(`Reconexão automática ${enabled ? 'habilitada' : 'desabilitada'}`);
  }

  /**
   * Try to reconnect to the last connected printer
   */
  tryAutoReconnect(): Observable<void> {
    if (!this.autoReconnectEnabled) {
      return throwError(
        () => new Error('Reconexão automática está desabilitada')
      );
    }

    const lastConnection = this.getLastConnection();
    if (!lastConnection) {
      return throwError(() => new Error('Nenhuma conexão anterior encontrada'));
    }

    // Verificar se já está conectado
    if (this.isConnected()) {
      this.log('Já está conectado à impressora');
      return from(Promise.resolve());
    }

    this.log(
      `Tentando reconectar automaticamente via ${
        lastConnection.transport === 'ble' ? 'Bluetooth' : 'Serial'
      }...`
    );

    if (lastConnection.transport === 'ble') {
      return this.connectBluetooth();
    } else {
      return this.connectSerial();
    }
  }

  /**
   * Clear last connection information
   */
  clearLastConnection(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      this.log('Informações da última conexão removidas');
    } catch (error) {
      this.log(`Erro ao remover informações da última conexão: ${error}`);
    }
  }

  /**
   * Print QR code
   * @param qrText Text to encode in QR code
   * @param preset Optional preset configuration (defaults to 50x30mm 8dpmm)
   * @param quantity Number of copies to print (default: 1)
   * @param textAbove Optional text to print above the QR code
   */
  printQRCode(
    qrText: string,
    preset?: Partial<NiimbotPreset>,
    quantity: number = 1,
    textAbove?: string
  ): Observable<void> {
    if (!this.client) {
      return throwError(() => new Error('Impressora não conectada'));
    }

    const finalPreset: NiimbotPreset = { ...this.defaultPreset, ...preset };

    return from(this.printQRCodeAsync(qrText, finalPreset, quantity, textAbove)).pipe(
      catchError((error) => {
        this.log(`Erro ao imprimir: ${error}`);
        return throwError(() => error);
      })
    );
  }

  private async printQRCodeAsync(
    qrText: string,
    preset: NiimbotPreset,
    quantity: number,
    textAbove?: string
  ): Promise<void> {
    if (!this.client) {
      throw new Error('Impressora não conectada');
    }

    const lib = await this.loadNiimbotLib();

    // Acessar ImageEncoder da biblioteca CDN
    const ImageEncoder = lib.ImageEncoder;

    if (!ImageEncoder || !ImageEncoder.encodeCanvas) {
      throw new Error('ImageEncoder não encontrado na biblioteca ou método encodeCanvas não disponível. Verifique se a biblioteca está corretamente carregada do CDN.');
    }

    // Create canvas with QR code and optional text above
    const canvas = await this.createLabelCanvas(qrText, preset, textAbove);

    // Get print direction from preset
    const printDirection: PrintDirectionType = preset.printDirection || 'top';

    // Encode canvas for printing
    const encoded = ImageEncoder.encodeCanvas(canvas, printDirection);

    // Get print task type (defaults to "B1" if auto-detection fails)
    const printTaskName = this.client.getPrintTaskType() ?? 'B1';

    const printTask = this.client.abstraction.newPrintTask(printTaskName, {
      totalPages: quantity,
      statusPollIntervalMs: 100,
      statusTimeoutMs: 8000,
    });

    try {
      await printTask.printInit();
      await printTask.printPage(encoded, quantity);
      await printTask.waitForFinished();
      this.log('Impressão concluída com sucesso');
    } catch (error) {
      throw error;
    } finally {
      await printTask.printEnd();
    }
  }

  /**
   * Print from label layout configuration
   * @param layout Label layout configuration with preset and elements
   * @param data Print data (QR text, participant name, lot name)
   * @param quantity Number of copies to print (default: 1)
   */
  printFromLayout(
    layout: LabelLayoutConfig,
    data: PrintData,
    quantity: number = 1
  ): Observable<void> {
    if (!this.client) {
      return throwError(() => new Error('Impressora não conectada'));
    }

    return from(this.printFromLayoutAsync(layout, data, quantity)).pipe(
      catchError((error) => {
        this.log(`Erro ao imprimir: ${error}`);
        return throwError(() => error);
      })
    );
  }

  private async printFromLayoutAsync(
    layout: LabelLayoutConfig,
    data: PrintData,
    quantity: number
  ): Promise<void> {
    if (!this.client) {
      throw new Error('Impressora não conectada');
    }

    const lib = await this.loadNiimbotLib();
    const ImageEncoder = lib.ImageEncoder;

    if (!ImageEncoder || !ImageEncoder.encodeCanvas) {
      throw new Error('ImageEncoder não encontrado na biblioteca ou método encodeCanvas não disponível. Verifique se a biblioteca está corretamente carregada do CDN.');
    }

    // Convert mm to pixels using dpmm (dots per mm)
    let widthPixels = Math.round(layout.preset.width * layout.preset.dpmm);
    const heightPixels = Math.round(layout.preset.height * layout.preset.dpmm);

    // Ensure width is multiple of 8 (required by NIIMBOT printers)
    widthPixels = this.roundToMultipleOf8(widthPixels);

    // Create main canvas
    const canvas = document.createElement('canvas');
    canvas.width = widthPixels;
    canvas.height = heightPixels;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Fill background with white
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calibração do cabeçote: desloca TUDO que é desenhado, depois do fundo
    // branco (que precisa cobrir a etiqueta inteira). Em mm no preset,
    // convertido para dots aqui — é o mesmo dpmm do resto do cálculo.
    const dpmm = layout.preset.dpmm;
    const offsetX = Math.round((layout.preset.offsetX ?? 0) * dpmm);
    const offsetY = Math.round((layout.preset.offsetY ?? 0) * dpmm);
    if (offsetX || offsetY) {
      this.log(`Deslocamento aplicado: ${layout.preset.offsetX ?? 0}mm x ${layout.preset.offsetY ?? 0}mm`);
      ctx.translate(offsetX, offsetY);
    }

    // Render each element
    for (const element of layout.elements) {
      if (element.type === 'qr') {
        // Render QR code
        const qrCanvas = await this.generateQRCodeCanvas(
          data.qrText,
          element.width,
          element.height
        );
        ctx.drawImage(qrCanvas, element.x, element.y);
      } else if (element.type === 'line') {
        // Render line (filled rectangle)
        ctx.fillStyle = 'black';
        ctx.fillRect(element.x, element.y, element.width, element.height);
      } else if (element.type === 'text') {
        // Resolve text value based on dataSource
        let textValue = '';
        switch (element.dataSource) {
          case 'fixed':
            textValue = element.value || '';
            break;
          case 'name':
            textValue = data.participantName || '';
            break;
          case 'lote':
            textValue = data.loteName || '';
            break;
          case 'extra':
            textValue = element.value || '';
            break;
        }

        // Build font string with all formatting properties
        const fontSize = element.fontSize || 12;
        const fontFamily = element.fontFamily || 'Arial';
        const fontWeight = element.fontWeight || 'normal';
        const fontStyle = element.fontStyle || 'normal';
        const textAlign = element.textAlign || 'left';

        ctx.fillStyle = 'black';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.textBaseline = 'top';

        // Calculate X position based on textAlign
        let textX = element.x;
        if (textAlign === 'center') {
          ctx.textAlign = 'center';
          textX = element.x + element.width / 2;
        } else if (textAlign === 'right') {
          ctx.textAlign = 'right';
          textX = element.x + element.width;
        } else {
          ctx.textAlign = 'left';
        }

        if (element.wrap) {
          this.drawWrappedText(ctx, textValue, textX, element.y, element.width, fontSize);
        } else {
          ctx.fillText(textValue, textX, element.y);
        }

        // Reset alignment
        ctx.textAlign = 'left';
      }
    }

    // Get print direction from preset
    const printDirection: PrintDirectionType = layout.preset.printDirection || 'top';

    // Encode canvas for printing
    const encoded = ImageEncoder.encodeCanvas(canvas, printDirection);

    // Get print task type (defaults to "B1" if auto-detection fails)
    const printTaskName = this.client.getPrintTaskType() ?? 'B1';

    const printTask = this.client.abstraction.newPrintTask(printTaskName, {
      totalPages: quantity,
      statusPollIntervalMs: 100,
      statusTimeoutMs: 8000,
    });

    try {
      await printTask.printInit();
      await printTask.printPage(encoded, quantity);
      await printTask.waitForFinished();
      this.log('Impressão concluída com sucesso');
    } catch (error) {
      throw error;
    } finally {
      await printTask.printEnd();
    }
  }

  /**
   * Draw text with word wrapping
   */
  private drawWrappedText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    fontSize: number
  ): void {
    const words = text.split(' ');
    let line = '';
    let currentY = y;
    const lineHeight = fontSize * 1.2; // 20% line spacing

    for (let i = 0; i < words.length; i++) {
      const testLine = line + (line ? ' ' : '') + words[i];
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth && line) {
        // Draw current line and move to next
        ctx.fillText(line, x, currentY);
        line = words[i];
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }

    // Draw remaining text
    if (line) {
      ctx.fillText(line, x, currentY);
    }
  }

  /**
   * Log message
   */
  private log(message: string): void {
    console.log(`[Printer] ${message}`);
    this.logSubject.next(message);
  }
}
